import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Paper, Typography, IconButton } from '@mui/material';
import { 
    ZoomIn, 
    ZoomOut, 
    Fullscreen
} from '@mui/icons-material';
import mapImage from '../assets/img/store-map.png';
import { MAIN_COLOR_RED } from '../assets/styles';



interface UserPosition {
    x: number;
    y: number;
    section: string;
    timestamp?: number;
    heading?: number;
    speed?: number;
}

interface MovementHistory {
    positions: UserPosition[];
    maxHistoryLength: number;
}

interface ProductLocation {
    productId: string;
    product?: any;
    x: number;
    y: number;
    quantity: number;
}

interface StoreMapProps {
    storeId: string;
    userPosition?: UserPosition;
    movementHistory?: MovementHistory;
    onSectionClick?: (sectionId: string) => void;
    productLocations?: ProductLocation[];
}

const StoreMap: React.FC<StoreMapProps> = ({
    userPosition: _userPosition, // currently unused (no blue marker)
    movementHistory,
    productLocations = [],
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({
        mouseX: 0,
        mouseY: 0,
        offsetX: 0,
        offsetY: 0,
    });
    const [showMovementPath] = useState(true);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [mapImageElement, setMapImageElement] = useState<HTMLImageElement | null>(null);

    // Load the map image
    const loadMapImage = useCallback(() => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Handle CORS issues
        img.onload = () => {
            setImageLoaded(true);
            setMapImageElement(img);
            // Set canvas size to match image
            if (canvasRef.current) {
                canvasRef.current.width = img.width;
                canvasRef.current.height = img.height;
            }
        };
        img.onerror = () => {
            console.error('Failed to load map image');
            // Fallback to a colored background if image fails to load
            setImageLoaded(true);
            setMapImageElement(null);
        };
        img.src = mapImage;
        return img;
    }, []);

    // Canvas drawing functions
    const drawMap = useCallback((ctx: CanvasRenderingContext2D) => {
        // Clear canvas with a light background
        ctx.fillStyle = '#f5f5f5';
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        
        // Apply transformations
        ctx.save();
        ctx.translate(offset.x, offset.y);
        ctx.scale(scale, scale);

        // Draw the map image as background if available
        if (mapImageElement) {
            ctx.drawImage(mapImageElement, 0, 0, mapImageElement.width, mapImageElement.height);
        } else {
            // Fallback background if image is not loaded
            ctx.fillStyle = '#e0e0e0';
            ctx.fillRect(0, 0, 500, 400);
            
            // Draw a simple grid pattern
            ctx.strokeStyle = '#ccc';
            ctx.lineWidth = 1;
            for (let i = 0; i < 500; i += 50) {
                ctx.beginPath();
                ctx.moveTo(i, 0);
                ctx.lineTo(i, 400);
                ctx.stroke();
            }
            for (let i = 0; i < 400; i += 50) {
                ctx.beginPath();
                ctx.moveTo(0, i);
                ctx.lineTo(500, i);
                ctx.stroke();
            }
        }



        // Draw movement path
        if (showMovementPath && movementHistory && movementHistory.positions.length > 1) {
            ctx.strokeStyle = 'rgba(33, 150, 243, 0.6)';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            ctx.beginPath();
            movementHistory.positions.forEach((pos, index) => {
                if (index === 0) {
                    ctx.moveTo(pos.x, pos.y);
                } else {
                    ctx.lineTo(pos.x, pos.y);
                }
            });
            ctx.stroke();
            
            // Draw movement trail dots
            movementHistory.positions.forEach((pos, index) => {
                const alpha = (index + 1) / movementHistory.positions.length;
                ctx.fillStyle = `rgba(33, 150, 243, ${alpha * 0.8})`;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 2, 0, 2 * Math.PI);
                ctx.fill();
            });
        }

        // Draw product locations
        if (productLocations && productLocations.length > 0) {
            productLocations.forEach((productLoc) => {
                const x = productLoc.x;
                const y = productLoc.y;
                
                // Draw product marker (red circle with white border)
                ctx.fillStyle = MAIN_COLOR_RED;
                ctx.beginPath();
                ctx.arc(x, y, 10, 0, 2 * Math.PI);
                ctx.fill();
                
                ctx.strokeStyle = '#fff';
                ctx.lineWidth = 2;
                ctx.stroke();
                
                // Draw quantity badge
                if (productLoc.quantity > 1) {
                    ctx.fillStyle = '#fff';
                    ctx.font = 'bold 10px Arial';
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(productLoc.quantity.toString(), x, y);
                }
            });
        }

        ctx.restore();
    }, [scale, offset, showMovementPath, mapImageElement, movementHistory, productLocations]);

    // Mouse / touch panning handlers
    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        setIsDragging(true);
        setDragStart({
            mouseX: e.clientX,
            mouseY: e.clientY,
            offsetX: offset.x,
            offsetY: offset.y,
        });
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (isDragging) {
            const dx = e.clientX - dragStart.mouseX;
            const dy = e.clientY - dragStart.mouseY;
            // Very strong multiplier so even small drags move the map a lot
            const speed = 32;
            setOffset({
                x: dragStart.offsetX + dx * speed,
                y: dragStart.offsetY + dy * speed,
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.5, Math.min(3, scale * delta));
        setScale(newScale);
    };

    // Zoom controls
    const zoomIn = () => setScale(prev => Math.min(3, prev * 1.2));
    const zoomOut = () => setScale(prev => Math.max(0.5, prev / 1.2));
    const resetView = () => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    };

    // Canvas setup and drawing
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Load image on mount
        if (!imageLoaded) {
            loadMapImage();
        }
        
        // Draw whenever dependencies change
        drawMap(ctx);
    }, [drawMap, imageLoaded, loadMapImage]);

    // Force redraw when dependencies in drawMap change
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        drawMap(ctx);
    }, [drawMap]);

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold">
                    Store Layout
                </Typography>
            </Box>

            <Box sx={{ position: 'relative', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                <canvas
                    ref={canvasRef}
                    style={{
                        cursor: isDragging ? 'grabbing' : 'grab',
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        touchAction: 'none' // Prevent default touch actions on mobile
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onWheel={handleWheel}
                    onTouchStart={(e) => {
                        const touch = e.touches[0];
                        const rect = canvasRef.current?.getBoundingClientRect();
                        if (rect) {
                            const x = touch.clientX - rect.left;
                            const y = touch.clientY - rect.top;
                            handleMouseDown({ clientX: x, clientY: y } as any);
                        }
                    }}
                    onTouchMove={(e) => {
                        const touch = e.touches[0];
                        const rect = canvasRef.current?.getBoundingClientRect();
                        if (rect) {
                            const x = touch.clientX - rect.left;
                            const y = touch.clientY - rect.top;
                            handleMouseMove({ clientX: x, clientY: y } as any);
                        }
                    }}
                    onTouchEnd={(_e) => {
                        handleMouseUp();
                    }}
                />
                
                {/* Zoom controls */}
                <Box sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10, 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 0.5 
                }}>
                    <IconButton 
                        size="small" 
                        onClick={zoomIn}
                        sx={{ bgcolor: 'white', boxShadow: 1 }}
                    >
                        <ZoomIn fontSize="small" />
                    </IconButton>
                    <IconButton 
                        size="small" 
                        onClick={zoomOut}
                        sx={{ bgcolor: 'white', boxShadow: 1 }}
                    >
                        <ZoomOut fontSize="small" />
                    </IconButton>
                    <IconButton 
                        size="small" 
                        onClick={resetView}
                        sx={{ bgcolor: 'white', boxShadow: 1 }}
                    >
                        <Fullscreen fontSize="small" />
                    </IconButton>
                </Box>

            </Box>


        </Paper>
    );
};

export default StoreMap;
